import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory } from '@fortawesome/free-solid-svg-icons';
import changeTrackingService from '../../services/changeTrackingService';
import { FieldChange } from '../../types/article';
import api from '../../services/api';
import { useAuth } from '../../auth/AuthContext';

interface BackendChangeIndicatorProps {
  fieldName: string;
  fieldPath: string;
  children: React.ReactNode;
  articleNumber?: string;
  isSupplierMode?: boolean;
}

// Global cache to avoid loading the same article changes multiple times
const articleChangesCache: { [articleNumber: string]: { changes: any[], timestamp: number } } = {};
const CACHE_DURATION = 60000; // 1 minute cache

// Global loading tracker to prevent too many simultaneous requests
const loadingTracker: { [articleNumber: string]: boolean } = {};

// Check if field has been viewed (from backend API in future)
const isFieldViewed = async (articleNumber: string, fieldPath: string): Promise<boolean> => {
  try {
    // TODO: Call backend API to check if field has been viewed
    // For now, return false to always show changes until marked as viewed
    return false;
  } catch (error) {
    console.error('Error checking field viewed status:', error);
    return false;
  }
};

const markFieldAsViewed = async (articleNumber: string, fieldPath: string, userId: string) => {
  try {
    await api.post(`/articles/${articleNumber}/mark-field-viewed`, {
      fieldPath,
      userId
    });
    console.log(`Field ${fieldPath} marked as viewed in backend`);
  } catch (error) {
    console.error('Error marking field as viewed:', error);
  }
};

interface BackendChangeIndicatorProps {
  fieldName: string;
  fieldPath: string;
  children: React.ReactNode;
  articleNumber?: string;
  isSupplierMode?: boolean;
}

export const BackendChangeIndicator: React.FC<BackendChangeIndicatorProps> = ({
  fieldName,
  fieldPath,
  children,
  articleNumber,
  isSupplierMode = false
}) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [hasBeenViewed, setHasBeenViewed] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [fieldHistory, setFieldHistory] = useState<FieldChange[]>([]);
  const [loading, setLoading] = useState(false);
  const [changesLoaded, setChangesLoaded] = useState(false); // Track if changes have been loaded
  const [initialCheckDone, setInitialCheckDone] = useState(false); // Track if initial check for red dots is done
  const { user } = useAuth(); // Get current user for tracking

  // **INITIAL LIGHTWEIGHT CHECK: Only check if there are changes for red dot display**
  useEffect(() => {
    if (!articleNumber || isSupplierMode || initialCheckDone) return;
    
    const doInitialCheck = async () => {
      try {
        // Check cache first for quick lookup
        const cached = articleChangesCache[articleNumber];
        const now = Date.now();
        
        if (cached && (now - cached.timestamp) < CACHE_DURATION) {
          // Use cached data
          const fieldHasChanges = cached.changes.some(change => change.field === fieldPath);
          setHasChanges(fieldHasChanges);
          console.log(`[BackendChangeIndicator] Initial check from cache for ${fieldName}: hasChanges=${fieldHasChanges}`);
        } else {
          // Do a lightweight check just for this article (will be cached for other components)
          if (!loadingTracker[articleNumber]) {
            loadingTracker[articleNumber] = true;
            
            try {
              console.log(`[BackendChangeIndicator] Initial check loading data for ${articleNumber}`);
              const changesData = await changeTrackingService.getArticleChanges(articleNumber);
              
              // Cache the result for other components
              articleChangesCache[articleNumber] = {
                changes: changesData.changes,
                timestamp: now
              };
              
              const fieldHasChanges = changesData.changes.some(change => change.field === fieldPath);
              setHasChanges(fieldHasChanges);
              console.log(`[BackendChangeIndicator] Initial check for ${fieldName}: hasChanges=${fieldHasChanges}`);
              
            } finally {
              loadingTracker[articleNumber] = false;
            }
          }
        }
        
        setInitialCheckDone(true);
        
      } catch (error) {
        console.error(`Error in initial check for ${fieldName}:`, error);
        setHasChanges(false);
        setInitialCheckDone(true);
      }
    };

    doInitialCheck();
  }, [articleNumber, fieldPath, isSupplierMode, fieldName, initialCheckDone]);

  // **DETAILED LOADING: Load full data when component becomes visible or is interacted with**
  const loadChanges = async () => {
    if (!articleNumber || isSupplierMode || changesLoaded) return;
    
    // Prevent multiple simultaneous requests for the same article
    if (loadingTracker[articleNumber]) {
      console.log(`[BackendChangeIndicator] Already loading changes for ${articleNumber}, waiting...`);
      
      // Wait for the loading to finish
      while (loadingTracker[articleNumber]) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Now use cached data
      const cached = articleChangesCache[articleNumber];
      if (cached) {
        const fieldHasChanges = cached.changes.some(change => change.field === fieldPath);
        setHasChanges(fieldHasChanges);
        
        const wasViewed = await isFieldViewed(articleNumber, fieldPath);
        setHasBeenViewed(wasViewed);
        
        setChangesLoaded(true);
      }
      return;
    }
    
    try {
      let changesData;
      
      // Check cache first
      const cached = articleChangesCache[articleNumber];
      const now = Date.now();
      
      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        // Use cached data
        changesData = { changes: cached.changes };
        console.log(`[BackendChangeIndicator] Using cached data for detailed load ${articleNumber}`);
      } else {
        // Load fresh data only if not recently loaded
        console.log(`[BackendChangeIndicator] Loading fresh data for detailed load ${articleNumber}`);
        loadingTracker[articleNumber] = true;
        
        try {
          changesData = await changeTrackingService.getArticleChanges(articleNumber);
          
          // Cache the result
          articleChangesCache[articleNumber] = {
            changes: changesData.changes,
            timestamp: now
          };
        } finally {
          loadingTracker[articleNumber] = false;
        }
      }
      
      // Check if this specific field has changes
      const fieldHasChanges = changesData.changes.some(change => change.field === fieldPath);
      setHasChanges(fieldHasChanges);
      
      // Check persistent viewed status (this is the detailed check)
      const wasViewed = await isFieldViewed(articleNumber, fieldPath);
      setHasBeenViewed(wasViewed);
      
      console.log(`[BackendChangeIndicator] Detailed load for ${fieldName} (${fieldPath}): hasChanges=${fieldHasChanges}, wasViewed=${wasViewed}`);
      
      setChangesLoaded(true);
      
    } catch (error) {
      console.error(`Error loading detailed changes for ${fieldName}:`, error);
      setHasChanges(false);
    }
  };

  // **INTERSECTION OBSERVER: Load detailed data when component becomes visible**
  useEffect(() => {
    if (!articleNumber || isSupplierMode || changesLoaded || !initialCheckDone) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !changesLoaded) {
            console.log(`[BackendChangeIndicator] ${fieldName} became visible, loading detailed data...`);
            loadChanges();
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.querySelector(`[data-field-path="${fieldPath}"]`);
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [articleNumber, fieldPath, isSupplierMode, changesLoaded, initialCheckDone]);

  // If supplier mode, just return children (no history functionality for suppliers)
  if (isSupplierMode) {
    return <>{children}</>;
  }

  const handleRightClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!articleNumber) return;

    // **LOAD CHANGES IF NOT YET LOADED**
    if (!changesLoaded) {
      await loadChanges();
    }

    // Show history modal immediately
    setShowHistory(true);
    setLoading(true);
    
    // Mark as viewed immediately (hide red dot) and persist it to backend
    setHasBeenViewed(true);
    if (user?.username) {
      await markFieldAsViewed(articleNumber, fieldPath, user.username);
    }
    
    console.log(`[BackendChangeIndicator] Field ${fieldName} marked as viewed`);

    try {
      const history = await changeTrackingService.getFieldHistory(articleNumber, fieldPath);
      setFieldHistory(history);
    } catch (error) {
      console.error(`Error loading field history for ${fieldName}:`, error);
      setFieldHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const closeHistory = () => {
    setShowHistory(false);
  };

  // Show red dot only if has changes AND not viewed yet
  const shouldShowRedDot = hasChanges && !hasBeenViewed;

  return (
    <div className="relative" data-field-path={fieldPath}>
      <div onContextMenu={handleRightClick} style={{ cursor: 'context-menu' }}>
        {children}
      </div>
      
      {/* Red indicator - only show if not viewed yet */}
      {shouldShowRedDot && (
        <div 
          className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white transform translate-x-1 -translate-y-1 z-10 cursor-pointer"
          title={`Feld geändert: ${fieldName}. Rechtsklick für Historie.`}
          onContextMenu={handleRightClick}
        />
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeHistory}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-96 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                <FontAwesomeIcon icon={faHistory} className="mr-2" />
                Historie: {fieldName}
              </h3>
              <button
                onClick={closeHistory}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            {loading ? (
              <div className="text-center py-4">Lade Historie...</div>
            ) : fieldHistory.length === 0 ? (
              <div className="text-center py-4 text-gray-500">Keine Historie verfügbar</div>
            ) : (
              <div className="space-y-4">
                {fieldHistory.map((change, index) => (
                  <div key={index} className="border-b pb-3">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm text-gray-600">
                        {new Date(change.changedAt).toLocaleString('de-DE')}
                      </span>
                      <span className="text-sm font-medium text-blue-600">
                        {change.changedBy}
                      </span>
                    </div>
                    <div className="text-sm">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <span className="text-gray-500">Alt:</span>
                          <div className="bg-red-50 p-2 rounded mt-1">
                            {change.oldValue || <em>Leer</em>}
                          </div>
                        </div>
                        <div className="flex-1">
                          <span className="text-gray-500">Neu:</span>
                          <div className="bg-green-50 p-2 rounded mt-1">
                            {change.newValue || <em>Leer</em>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
